import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  expertReportKeys,
  fetchExpertReportBlocks,
  useExpertReports,
  type ExpertReportBlockTreeNode,
} from '@/entities/expert-report';

/** Группа каталога: экспертное заключение и имена его критериев (блоков верхнего уровня). */
export type CriteriaCatalogGroup = {
  reportId: number;
  reportName: string;
  /** Имена критериев отчёта; вместе с `reportId` адресуют скрытый критерий. */
  criteria: string[];
};

/**
 * Собирает имена критериев отчёта — это блоки **верхнего уровня** его дерева (прямые потомки
 * корня), в порядке отчёта (`orderPosition`). Именно верхнеуровневые блоки, а не листовые:
 * дашборд считает средние баллы и алерты по оценкам блоков верхнего уровня (бэк —
 * `_extract_blocks_from_evaluation`), поэтому «критерий» на дашборде — это верхнеуровневый блок,
 * а листовой блок — его под-пункт, отдельным критерием не выступающий. Имя верхнеуровневого
 * блока вместе с его заключением и адресует критерий в настройках отображения.
 */
const collectCriteriaNames = (blocks: ExpertReportBlockTreeNode[]): string[] =>
  [...blocks].sort((a, b) => a.orderPosition - b.orderPosition).map((block) => block.name);

/**
 * Полный каталог критериев всех экспертных заключений, сгруппированный по отчётам-владельцам.
 * Критерии отчёта — его блоки верхнего уровня (та же гранулярность, что и «критерий» на дашборде),
 * в порядке отчёта.
 *
 * Источник — entity `expert-report` (список заключений + деревья их блоков), а не критерии текущей
 * выборки дашборда: в настройках отображения нужно скрыть и вернуть любой критерий, в том числе
 * отсутствующий в выбранном периоде. Деревья блоков грузятся тем же кэшируемым методом, что и
 * настройка колонок таблицы диалогов.
 */
export const useCriteriaCatalog = (): { groups: CriteriaCatalogGroup[]; isLoading: boolean } => {
  // Предел 100 — потолок эндпоинта списка заключений; тот же, что в настройке колонок таблицы диалогов.
  const { data: reportsData, isLoading: reportsLoading } = useExpertReports({ limit: 100, isDeleted: false });
  const reports = useMemo(() => reportsData?.items ?? [], [reportsData?.items]);

  const blockQueries = useQueries({
    queries: reports.map((report) => ({
      queryKey: expertReportKeys.blocks(report.id),
      queryFn: () => fetchExpertReportBlocks(report.id),
      staleTime: 60_000,
    })),
  });

  const groups = useMemo(() => {
    return reports
      .map((report, index) => ({
        reportId: report.id,
        reportName: report.name,
        criteria: collectCriteriaNames(blockQueries[index]?.data?.blocks ?? []),
      }))
      .filter((group) => group.criteria.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports, blockQueries.map((q) => q.dataUpdatedAt).join('|')]);

  const isLoading = reportsLoading || blockQueries.some((q) => q.isLoading);

  return { groups, isLoading };
};

/** Ранги порядка отчётов и их критериев: элемент вне каталога уходит в конец. */
export type CriteriaOrder = {
  /** Ранг экспертного заключения в каталоге. */
  reportRank: (reportId: number) => number;
  /** Ранг критерия внутри своего заключения. */
  criterionRank: (reportId: number, name: string) => number;
};

/**
 * Порядок отчётов и критериев внутри них по каталогу — общий для всех карточек, обязанных
 * показывать их в одном и том же порядке (график средних баллов, сводка, настройки отображения):
 * `quality-summary` возвращает критерии в порядке бэка, не совпадающем с порядком блоков
 * в заключении.
 *
 * Ранг элемента вне каталога — `+∞`: при стабильной сортировке такие элементы остаются в конце
 * в исходном порядке.
 */
export const useCriteriaOrder = (): CriteriaOrder => {
  const { groups } = useCriteriaCatalog();

  return useMemo(() => {
    const reports = new Map<number, number>();
    const criteria = new Map<number, Map<string, number>>();
    groups.forEach((group, reportIndex) => {
      reports.set(group.reportId, reportIndex);
      criteria.set(group.reportId, new Map(group.criteria.map((name, index) => [name, index])));
    });

    const rank = (position: number | undefined): number => position ?? Number.POSITIVE_INFINITY;

    return {
      reportRank: (reportId) => rank(reports.get(reportId)),
      criterionRank: (reportId, name) => rank(criteria.get(reportId)?.get(name)),
    };
  }, [groups]);
};
